import { useState, useCallback, Suspense, useEffect } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { Box, Container } from '@mui/material'
import FolderIcon from '@mui/icons-material/Folder'
import AppBarComponent from '@/components/AppBarComponent'
import ContentContainer from '@/components/ContentContainer'
import LoadingPage from '@/components/LoadingPage'
import Welcome from '@/components/Welcome'
import SideMenuTree from '@/components/SideMenuTree/SideMenuTree'
import { useAuth } from '@/hooks/AuthContext'
import { checkAccess } from '@/utils/accessUtils'
import { useRecentModules } from '@/hooks/useRecentModules'

const menuItems = [
  { name: 'Analyst', path: '/stabilityMaintenanceModules/analyst' },
  { name: 'Abbreviation', path: '/stabilityMaintenanceModules/abbreviation' },
  {
    name: 'Packaging Dictionary',
    folderIcon: <FolderIcon />,
    children: [
      {
        name: 'Packaging Size',
        path: '/stabilityMaintenanceModules/packagingSize'
      },
      {
        name: 'Container',
        path: '/stabilityMaintenanceModules/container'
      },
      {
        name: 'Container Resin',
        path: '/stabilityMaintenanceModules/containerResin'
      },
      {
        name: 'Container Manufacturer',
        path: '/stabilityMaintenanceModules/containerManufacturer'
      },
      {
        name: 'Closure',
        path: '/stabilityMaintenanceModules/closure'
      },
      {
        name: 'Closure Manufacturer',
        path: '/stabilityMaintenanceModules/closureManufacturer'
      },
      {
        name: 'InnerSeal',
        path: '/stabilityMaintenanceModules/innerSeal'
      },
      {
        name: 'Liner',
        path: '/stabilityMaintenanceModules/liner'
      },
      {
        name: 'Liner Manufacturer',
        path: '/stabilityMaintenanceModules/linerManufacturer'
      },
      {
        name: 'Filler',
        path: '/stabilityMaintenanceModules/filler'
      },
      {
        name: 'Filler Manufacturer',
        path: '/stabilityMaintenanceModules/fillerManufacturer'
      },
      {
        name: 'Desiccant',
        path: '/stabilityMaintenanceModules/desiccant'
      },
      {
        name: 'Desiccant Manufacturer',
        path: '/stabilityMaintenanceModules/desiccantManufacturer'
      },
      {
        name: 'Blisters',
        path: '/stabilityMaintenanceModules/blisters'
      },
      {
        name: 'Lidding Foil',
        path: '/stabilityMaintenanceModules/liddingFoil'
      },
      {
        name: 'Forming Foil',
        path: '/stabilityMaintenanceModules/formingFoil'
      },
      {
        name: 'Other',
        path: '/stabilityMaintenanceModules/other'
      },
      {
        name: 'Other Manufacturer',
        path: '/stabilityMaintenanceModules/otherManufacturer'
      }
    ]
  },
  { name: 'QuickList', path: '/stabilityMaintenanceModules/quickList' },
  {
    name: 'Reason For Study',
    path: '/stabilityMaintenanceModules/reasonForStudy'
  },
  { name: 'Site', path: '/stabilityMaintenanceModules/site' },
  {
    name: 'Storage Location',
    path: '/stabilityMaintenanceModules/storageLocation'
  },
  {
    name: 'Study Condition',
    path: '/stabilityMaintenanceModules/studyCondition'
  },
  {
    name: 'User Defined Fields',
    path: '/stabilityMaintenanceModules/userDefinedFields'
  },
  {
    name: 'Annual Log Report',
    path: '/stabilityMaintenanceModules/annualLogReport'
  },
  { name: 'Lab Number', path: '/stabilityMaintenanceModules/labNumber' },
  { name: 'Measurement', path: '/stabilityMaintenanceModules/measurement' },
  {
    name: 'Specification Library',
    folderIcon: <FolderIcon />,
    children: [
      {
        name: 'Final Approval',
        path: '/stabilityMaintenanceModules/finalProduct'
      },
      {
        name: 'Edit Product',
        path: '/stabilityMaintenanceModules/editProduct'
      },
      {
        name: 'View  Product',
        path: '/stabilityMaintenanceModules/viewProduct'
      },
      {
        name: 'Test Library',
        path: '/stabilityMaintenanceModules/testLibrary'
      },
      {
        name: 'Edit Log Report',
        path: '/stabilityMaintenanceModules/editLogReport'
      }
    ]
  }
]

export default function StabilityMaintenanceModules() {
  const { user, specApprovalLevel } = useAuth()
  const location = useLocation()
  const { recordVisit } = useRecentModules()
  const isChildRouteActive =
    location.pathname.startsWith('/stabilityMaintenanceModules/') ||
    (location.pathname.startsWith('/stabilityMaintenanceModules') &&
      location.pathname.length > '/stabilityMaintenanceModules'.length)

  useEffect(() => { recordVisit('/stabilityMaintenanceModules', 'Stability Maintenance') }, [])

  const [collapsed, setCollapsed] = useState(false)

  const handleToggle = useCallback(() => {
    setCollapsed(prev => !prev)
  }, [])

  // Dynamic menu items with privilege check
  const getMenuItems = () => {
    const rawItems = [
      { name: 'Analyst', path: '/stabilityMaintenanceModules/analyst' },
      { name: 'Abbreviation', path: '/stabilityMaintenanceModules/abbreviation' },
      {
        name: 'Packaging Dictionary',
        folderIcon: <FolderIcon />,
        children: [
          { name: 'Packaging Size', path: '/stabilityMaintenanceModules/packagingSize' },
          { name: 'Container', path: '/stabilityMaintenanceModules/container' },
          { name: 'Container Resin', path: '/stabilityMaintenanceModules/containerResin' },
          {
            name: 'Container Manufacturer',
            path: '/stabilityMaintenanceModules/containerManufacturer'
          },
          { name: 'Closure', path: '/stabilityMaintenanceModules/closure' },
          {
            name: 'Closure Manufacturer',
            path: '/stabilityMaintenanceModules/closureManufacturer'
          },
          { name: 'InnerSeal', path: '/stabilityMaintenanceModules/innerSeal' },
          { name: 'Liner', path: '/stabilityMaintenanceModules/liner' },
          { name: 'Liner Manufacturer', path: '/stabilityMaintenanceModules/linerManufacturer' },
          { name: 'Filler', path: '/stabilityMaintenanceModules/filler' },
          { name: 'Filler Manufacturer', path: '/stabilityMaintenanceModules/fillerManufacturer' },
          { name: 'Desiccant', path: '/stabilityMaintenanceModules/desiccant' },
          {
            name: 'Desiccant Manufacturer',
            path: '/stabilityMaintenanceModules/desiccantManufacturer'
          },
          { name: 'Blisters', path: '/stabilityMaintenanceModules/blisters' },
          { name: 'Lidding Foil', path: '/stabilityMaintenanceModules/liddingFoil' },
          { name: 'Forming Foil', path: '/stabilityMaintenanceModules/formingFoil' },
          { name: 'Other', path: '/stabilityMaintenanceModules/other' },
          { name: 'Other Manufacturer', path: '/stabilityMaintenanceModules/otherManufacturer' }
        ]
      },
      { name: 'QuickList', path: '/stabilityMaintenanceModules/quickList' },
      { name: 'Reason For Study', path: '/stabilityMaintenanceModules/reasonForStudy' },
      { name: 'Site', path: '/stabilityMaintenanceModules/site' },
      { name: 'Storage Location', path: '/stabilityMaintenanceModules/storageLocation' },
      { name: 'Study Condition', path: '/stabilityMaintenanceModules/studyCondition' },
      { name: 'User Defined Fields', path: '/stabilityMaintenanceModules/userDefinedFields' },
      { name: 'Annual Log Report', path: '/stabilityMaintenanceModules/annualLogReport' },
      { name: 'Lab Number', path: '/stabilityMaintenanceModules/labNumber' },
      { name: 'Measurement', path: '/stabilityMaintenanceModules/measurement' },
      {
        name: 'Specification Library',
        folderIcon: <FolderIcon />,
        children: [
          { name: 'Edit Product', path: '/stabilityMaintenanceModules/editProduct' },
          ...(specApprovalLevel !== 'N' && specApprovalLevel !== 'S'
            ? [{ name: 'Level 1 Approval', path: '/stabilityMaintenanceModules/level1Approval' }]
            : []),
          ...(specApprovalLevel !== 'N'
            ? [{ name: 'Final Approval', path: '/stabilityMaintenanceModules/finalProduct' }]
            : []),
          { name: 'View Product', path: '/stabilityMaintenanceModules/viewProduct' },
          { name: 'Edit Log Report', path: '/stabilityMaintenanceModules/editLogReport' },
          { name: 'Test Library', path: '/stabilityMaintenanceModules/testLibrary' }
        ]
      }
    ]

    // Apply privilege checks recursively
    const processItems = items => {
      return items.map(item => {
        const hasDirectAccess = checkAccess(user, item.name)

        if (item.children) {
          const processedChildren = processItems(item.children)
          // A folder is enabled if:
          // 1. User has direct access to the folder name (e.g. ADMIN override or specific folder privilege)
          // 2. OR at least one child is NOT disabled
          const anyChildEnabled = processedChildren.some(child => !child.disabled)

          return {
            ...item,
            disabled: !hasDirectAccess && !anyChildEnabled,
            children: processedChildren
          }
        }

        return {
          ...item,
          disabled: !hasDirectAccess
        }
      })
    }

    return processItems(rawItems)
  }

  const items = getMenuItems()

  return (
    <Container
      disableGutters
      maxWidth={false}
      sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}
    >
      <AppBarComponent username={user?.username} title="Stability Maintenance Modules" />
      <Box display="flex" flexGrow={1}>
        <SideMenuTree items={items} collapsed={collapsed} onToggle={handleToggle} />
        <ContentContainer>
          <Suspense fallback={<LoadingPage />}>
            {isChildRouteActive ? (
              <Outlet />
            ) : (
              <Welcome moduleName="Stability Maintenance Modules" />
            )}
          </Suspense>
        </ContentContainer>
      </Box>
    </Container>
  )
}
